<template>
    <section id="accommodation">
        <h2>Accommodation</h2>

        <div v-if="family && family.has_venue_accommodation">
            <div class="reserved-accommodation">
                <p>There is limited accommodation available at the venue. We would love for you to stay so we have reserved a room for you. If you would like to book at the venue, please contact us directly for details about the room, cost, and booking process.</p>
                <p class="reserved-accommodation-hint">* Please note that due to limited space, this offer of on-site accommodation is only available to guests who appear on this invitation.</p>
            </div>

            <p>If you would rather stay elsewhere, these are some of the places we recommend, but there are many more to choose from nearby.</p>
        </div>

        <p v-if="(family && !family.has_venue_accommodation)" class="mb-4">
            There is a large selection of beautiful spots to stay in the area. These are some of the ones we recommend.
        </p>

        <div class="accommodation-options">
            <div ref="hotel1" class="hotel-option mb-6 slide-left" :class="{ 'slide-in': visibleHotels.hotel1 }">
                <h3 class="mb-2">Troutbagger Farm</h3>
                <p class="mb-1">
                    <a href="https://www.booking.com/hotel/za/troutbagger-farm.en-gb.html" target="_blank" class="location-link">
                        www.booking.com/hotel/za/troutbagger-farm
                    </a>
                </p>
                <p class="mb-1">Phone: +27 83 270 3817</p>
                <p class="mb-2">Distance: 1.1 km from venue</p>
            </div>

            <div ref="hotel2" class="hotel-option mb-6 slide-left" :class="{ 'slide-in': visibleHotels.hotel2 }">
                <h3 class="mb-2">Springholm Cottages</h3>
                <p class="mb-1">
                    <a href="https://springholmcottages.co.za/" target="_blank" class="location-link">
                        www.springholmcottages.co.za
                    </a>
                </p>
                <p class="mb-1">Phone: +27 33 266 6965</p>
                <p class="mb-2">Distance: 2 km from venue</p>
            </div>

            <div ref="hotel3" class="hotel-option mb-6 slide-left" :class="{ 'slide-in': visibleHotels.hotel3 }">
                <h3 class="mb-2">Oakridge Farm Cottage</h3>
                <p class="mb-1">
                    <a href="https://oakridgefarmcottage.co.za/" target="_blank" class="location-link">
                        www.oakridgefarmcottage.co.za
                    </a>
                </p>
                <p class="mb-1">Phone: +27 82 880 2184</p>
                <p class="mb-2">Distance: 4.3 km from venue</p>
            </div>

            <div ref="hotel4" class="hotel-option mb-6 slide-left" :class="{ 'slide-in': visibleHotels.hotel4 }">
                <h3 class="mb-2">Kildare Cottages</h3>
                <p class="mb-1">
                    <a href="https://kildarecottages.co.za/" target="_blank" class="location-link">
                        www.kildarecottages.co.za
                    </a>
                </p>
                <p class="mb-1">Phone: +27 83 306 3900</p>
                <p class="mb-2">Distance: 6.2 km from venue</p>
            </div>

            <div ref="hotel5" class="hotel-option mb-6 slide-left" :class="{ 'slide-in': visibleHotels.hotel5 }">
                <h3 class="mb-2">Fordoun Hotel & Spa</h3>
                <p class="mb-1">
                    <a href="https://fordoun.com/" target="_blank" class="location-link">
                        www.fordoun.com
                    </a>
                </p>
                <p class="mb-1">Phone: +27 33 266 6217</p>
                <p class="mb-2">Distance: 8.2 km from venue</p>
            </div>

            <div ref="hotel6" class="hotel-option mb-6 slide-left" :class="{ 'slide-in': visibleHotels.hotel6 }">
                <h3 class="mb-2">Nottingham Road Hotel</h3>
                <p class="mb-1">
                    <a href="https://www.nottieshotel.co.za/" target="_blank" class="location-link">
                        www.nottieshotel.co.za
                    </a>
                </p>
                <p class="mb-1">Phone: +27 33 266 6151 </p>
                <p class="mb-2">Distance: 12.2 km from venue</p>
            </div>

            <div ref="hotel7" class="hotel-option slide-left" :class="{ 'slide-in': visibleHotels.hotel7 }">
                <h3 class="mb-2">Thyme Out | Weltevreden Manor</h3>
                <p class="mb-1">
                    <a href="https://www.wvmanor.com/self-catering-accommodation/" target="_blank" class="location-link">
                        www.wvmanor.com
                    </a>
                </p>
                <p class="mb-1">Phone: +27 82 506 6409</p>
                <p class="mb-2">Distance: 13.2 km from venue</p>
            </div>
        </div>
	</section>
</template>

<script>
export default {
    name: "AccommodationSection",

    props: {
        family: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            visibleHotels: {
                hotel1: false,
                hotel2: false,
                hotel3: false,
                hotel4: false,
                hotel5: false,
                hotel6: false,
                hotel7: false
            }
        }
    },

    mounted() {
        this.setupIntersectionObserver()
    },

    methods: {
        setupIntersectionObserver() {
            const options = {
                root: null,
                rootMargin: '0px',
                threshold: 0.1
            };

            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const hotelName = this.getHotelName(entry.target);
                        if (hotelName && !this.visibleHotels[hotelName]) {
                            this.visibleHotels[hotelName] = true;
                        }
                    }
                });
            }, options);

            // Observe all hotel options
            this.$nextTick(() => {
                const hotels = [
                    'hotel1', 'hotel2', 'hotel3', 'hotel4',
                    'hotel5', 'hotel6', 'hotel7'
                ];

                hotels.forEach(hotelRef => {
                    if (this.$refs[hotelRef]) {
                        observer.observe(this.$refs[hotelRef]);
                    }
                });
            });
        },

        getHotelName(element) {
            const refMap = {
                'hotel1': 'hotel1',
                'hotel2': 'hotel2',
                'hotel3': 'hotel3',
                'hotel4': 'hotel4',
                'hotel5': 'hotel5',
                'hotel6': 'hotel6',
                'hotel7': 'hotel7'
            };

            for (const [ref, name] of Object.entries(refMap)) {
                if (this.$refs[ref] === element) {
                    return name;
                }
            }
            return null;
        }
    }
}
</script>

<style scoped>
.reserved-accommodation {
    background-color: #02721820;
    padding: 1rem;
    border-radius: 8px;
    border: 1px solid #027218;
    margin: 2rem 0;
}

.reserved-accommodation-hint {
    font-size: 0.8rem;
    color: #666;
    margin-top: 0.5rem;
}

.accommodation-options {
	margin-top: 2rem;
}

.hotel-option {
	padding: 1rem;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	background-color: #fafafa;
	opacity: 0;
	transition: all 0.8s ease-out;
}

.hotel-option h3 {
	margin-bottom: 0.5rem;
}

.location-link {
	color: #1976d2;
	text-decoration: none;
}

.location-link:hover {
	text-decoration: underline;
}

/* Left sliding hotels */
.hotel-option.slide-left {
    transform: translateX(-100px);
}

.hotel-option.slide-left.slide-in {
    opacity: 1;
    transform: translateX(0);
}

/* Right sliding hotels */
.hotel-option.slide-right {
    transform: translateX(100px);
}

.hotel-option.slide-right.slide-in {
    opacity: 1;
    transform: translateX(0);
}

/* Disable animations during programmatic scroll */
.disable-animations .hotel-option {
    transition: none !important;
    opacity: 1 !important;
    transform: translateX(0) !important;
}
</style>